/**
 * User-facing viewer readiness — when the splat is visible or mount-eligible.
 * Shared by uploadProgress (pipeline UI) and viewerLoadContract (mount gates).
 */

/**
 * Shared mobile + desktop direct-PLY viewer limit. Canonical `.ply` at or under this size loads
 * directly (no SOG needed). Keep in sync with functions PLY_DIRECT_VIEWER_MAX_BYTES.
 */
export const PLY_DIRECT_VIEWER_MAX_BYTES = 10 * 1024 * 1024;

/** Best-effort canonical PLY size (bytes) from capture metadata. */
export function captureCanonicalPlyBytes(metadata) {
  const stor = metadata?.storage || {};
  const direct = stor.canonicalPlySizeBytes;
  if (typeof direct === 'number' && Number.isFinite(direct) && direct > 0) return direct;
  const mb = metadata?.processing?.splatSizeMB;
  const hasSizeEstimate = typeof mb === 'number' && Number.isFinite(mb) && mb > 0;
  if (!hasSizeEstimate) return null;
  const paths = [
    stor.splatPath,
    stor.originalSplatPath,
    stor.fallbackPlyPath,
  ].filter((p) => typeof p === 'string' && p.trim());
  const hasPlyReference = paths.some((p) => String(p).split('?')[0].toLowerCase().endsWith('.ply'));
  if (hasPlyReference) {
    return Math.round(mb * 1024 * 1024);
  }
  const splatPath = String(stor.splatPath || '');
  if (splatPath.split('?')[0].toLowerCase().endsWith('.ply')) {
    return Math.round(mb * 1024 * 1024);
  }
  return null;
}

/** Canonical PLY small enough to load directly (mobile + desktop) without an SOG. */
export function captureQualifiesForDirectPly(metadata) {
  const bytes = captureCanonicalPlyBytes(metadata);
  return typeof bytes === 'number' && bytes > 0 && bytes <= PLY_DIRECT_VIEWER_MAX_BYTES;
}

export function isViewerAssetConversionPending(metadata) {
  if (!metadata || typeof metadata !== 'object') return false;
  // Small PLY loads directly — never "converting".
  if (captureQualifiesForDirectPly(metadata)) return false;
  const stor = metadata.storage || {};
  const vs = String(stor.viewerStatus || '').toLowerCase();
  return vs === 'pending' || vs === 'processing' || vs === 'client-pending';
}

export function captureHasReconstructionArtifact(metadata) {
  if (!metadata || typeof metadata !== 'object') return false;
  const st = metadata.processing?.status;
  const splatPath = metadata.storage?.splatPath;
  const viewerStatus = metadata.storage?.viewerStatus;
  const viewerPath = metadata.storage?.viewerPath;
  return (
    (st === 'completed' && !!splatPath)
    || (viewerStatus === 'ready' && !!viewerPath)
    || (!!splatPath && Number(metadata.processing?.splatCount) > 0)
  );
}

export function isPipelineOrientationReadyForViewer(metadata) {
  if (!metadata || typeof metadata !== 'object') return true;
  if (metadata.userId === 'demo') return true;
  if (hasPersistedEditorTransform(metadata)) return true;
  const stor = metadata.storage || {};
  if (stor.orientationCorrected === true) return true;
  return false;
}

export function hasPersistedEditorTransform(metadata) {
  const et = metadata?.editorTransform;
  return !!(
    et
    && typeof et === 'object'
    && Array.isArray(et.position)
    && et.position.length === 3
    && Array.isArray(et.quaternion)
    && et.quaternion.length === 4
    && Array.isArray(et.scale)
    && et.scale.length === 3
  );
}

/**
 * True when the user should see "pipeline complete" and the splat can be shown (or is showing).
 * Large captures require the unified SOG viewer asset; small PLYs (<=10MB) load directly.
 */
export function isCaptureUserFacingComplete(metadata, { isActiveReupload = false } = {}) {
  if (isActiveReupload || !metadata || typeof metadata !== 'object') return false;
  const stor = metadata.storage || {};
  if (stor.viewerStatus === 'ready' && !!stor.viewerPath) return true;
  const procDone = String(metadata.processing?.status || '').toLowerCase() === 'completed';
  // Small canonical PLY is viewer-ready as-is once reconstruction completed.
  if (procDone && captureQualifiesForDirectPly(metadata) && !!stor.splatPath) {
    return true;
  }
  if (!procDone) return false;
  const splatPath = String(stor.splatPath || '');
  if (splatPath.split('?')[0].toLowerCase().endsWith('.sog')) return true;
  const viewerPath = String(stor.viewerPath || '');
  const viewerFmt = String(stor.viewerFormat || stor.splatFormat || '').toLowerCase();
  if (viewerPath && viewerFmt === 'sog') return true;
  return false;
}

/**
 * True when a viewer asset is ready to mount (SOG ready, or small PLY served directly).
 */
export function isCaptureViewerAssetReady(metadata) {
  if (!metadata || typeof metadata !== 'object') return false;
  const stor = metadata.storage || {};
  if (stor.viewerStatus === 'ready' && !!stor.viewerPath) return true;
  if (captureQualifiesForDirectPly(metadata) && !!stor.splatPath) return true;
  const splatPath = String(stor.splatPath || '');
  if (splatPath.split('?')[0].toLowerCase().endsWith('.sog')) return true;
  const viewerPath = String(stor.viewerPath || '');
  const viewerFmt = String(stor.viewerFormat || stor.splatFormat || '').toLowerCase();
  return !!(viewerPath && viewerFmt === 'sog');
}

/**
 * Pipeline UI "complete" messaging — SOG-ready only.
 */
export function isCaptureMessagingComplete(metadata, { isActiveReupload = false } = {}) {
  return isCaptureUserFacingComplete(metadata, { isActiveReupload });
}

/**
 * Reconstruction finished but viewer asset not ready — for status pills/cards/bar copy.
 */
export function isCaptureViewerPrepPendingForMessaging(metadata, { isActiveReupload = false } = {}) {
  if (isActiveReupload || !metadata || typeof metadata !== 'object') return false;
  if (isCaptureMessagingComplete(metadata, { isActiveReupload })) return false;
  const rawStatus = String(metadata.processing?.status || '').toLowerCase();
  if (rawStatus === 'completed') return true;
  return captureHasReconstructionArtifact(metadata);
}

/**
 * GPU/export artifact exists but viewer is not yet user-facing ready.
 */
export function isCaptureViewerPrepPendingFromMetadata(metadata, { isActiveReupload = false } = {}) {
  return isCaptureViewerPrepPendingForMessaging(metadata, { isActiveReupload });
}

export function shouldShowViewerOptimizationBadge() {
  return false;
}

export const VIEWER_OPTIMIZATION_BADGE_COPY = 'Optimizing viewer quality…';
